import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/services';
import {
  useGetServiceRequestInfiniteList,
  useGetServiceRequestList,
} from './useGetServiceRequestList';

export type UseDeleteServiceRequestPayload = {
  serviceRequestId: string;
};

export const useDeleteServiceRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteServiceRequest'],
    mutationFn: ({ serviceRequestId }: UseDeleteServiceRequestPayload) =>
      api
        .delete(`/v3/service_request/${serviceRequestId}`)
        .then((res) => res.data),
    onSuccess: (
      _: unknown,
      { serviceRequestId }: UseDeleteServiceRequestPayload
    ) => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequestList'] });

      /** Optimistically update the service request list */
      queryClient.setQueriesData<
        ReturnType<typeof useGetServiceRequestList>['data']
      >({ queryKey: ['serviceRequestList'] }, (oldData) => {
        if (!oldData?.serviceRequests?.length) return oldData;

        return {
          ...oldData,
          serviceRequests: oldData.serviceRequests.filter(
            (sr) => sr.service_request_id !== serviceRequestId
          ),
        };
      });

      queryClient.setQueriesData<
        ReturnType<typeof useGetServiceRequestInfiniteList>['data']
      >({ queryKey: ['serviceRequestList', 'infinite'] }, (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        const newPages = oldData.pages.map((page) => {
          return {
            ...page,
            serviceRequests: page.serviceRequests.filter(
              (sr) => sr.service_request_id !== serviceRequestId
            ),
          };
        });
        return { ...oldData, pages: newPages };
      });
    },
  });
};
